import React, { useEffect, useState } from "react";
import { Button, Table, Modal, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";

import {
  createSprint,
  getSprintsByOrgAndProject,
  mapTaskToSprint,
  mapTicketToSprint,
  getTasksBySprint,
  getTicketsBySprint,
  removeTaskFromSprint,
  removeTicketFromSprint,
  getSprintSummary,
} from "../Api/SprintProject";

import { getTasksByProject } from "../Api/TaskMaster";
import { getTicketsByProjectId } from "../Api/TicketApiOrg";

const SprintTab = ({ orgId, projectId, userId }) => {
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [selectedSprint, setSelectedSprint] = useState(null);
  const [sprintTasks, setSprintTasks] = useState([]);
  const [sprintTickets, setSprintTickets] = useState([]);

  const [projectTasks, setProjectTasks] = useState([]);
  const [projectTickets, setProjectTickets] = useState([]);

  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);

  const [sprintViewType, setSprintViewType] = useState("TASK");
  const [sprintTableViewType, setSprintTableViewType] = useState("TASK");
  const [sprintSummaries, setSprintSummaries] = useState({});

  const [formData, setFormData] = useState({
    sprintName: "",
    sprintStatus: "Planned",
    sprintTypes: "",
    sprintStartDate: "",
    sprintEndDate: "",
  });

  // ================= LOAD SPRINTS =================
  const loadSprints = async () => {
    if (!orgId || !projectId) return;

    try {
      setLoading(true);

      const [sprintData, summaryData] = await Promise.all([
        getSprintsByOrgAndProject(orgId, projectId),
        getSprintSummary(orgId, projectId),
      ]);

      console.log("Sprints:", sprintData);
      console.log("Sprint Summaries:", summaryData);

      setSprints(sprintData || []);

      const summaryMap = {};
      (summaryData || []).forEach((s) => {
        summaryMap[s.sprintId] = s;
      });

      console.log("Sprint Summaries:", summaryMap);

      setSprintSummaries(summaryMap);
    } catch {
      toast.error("Failed to load sprints");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSprints();
  }, [orgId, projectId]);

  // ================= CREATE =================
  const handleCreateSprint = async () => {
    if (!formData.sprintName.trim()) {
      toast.error("Sprint name required");
      return;
    }

    try {
      setSaving(true);

      await createSprint({
        orgId,
        sprintName: formData.sprintName,
        sprintStatus: formData.sprintStatus,
        sprintProject: projectId,
        sprintTypes: formData.sprintTypes,
        sprintStartDate: formData.sprintStartDate,
        sprintEndDate: formData.sprintEndDate,
        createdBy: userId,
      });

      toast.success("Sprint created");
      setShowModal(false);
      loadSprints();
    } catch {
      toast.error("Failed to create sprint");
    } finally {
      setSaving(false);
    }
  };

  // ================= OPEN DETAILS =================
  const openSprintDetails = async (sprint) => {
    try {
      setSelectedSprint(sprint);
      setShowDetailModal(true);

      const [tasks, tickets, allTasks, allTickets] = await Promise.all([
        getTasksBySprint(sprint.sprintId),
        getTicketsBySprint(sprint.sprintId),
        getTasksByProject(projectId),
        getTicketsByProjectId(projectId),
      ]);

      setSprintTasks(tasks || []);
      setSprintTickets(tickets || []);
      setProjectTasks(allTasks || []);
      setProjectTickets(allTickets || []);
    } catch {
      toast.error("Failed to load sprint details");
    }
  };

  // ================= MAP =================
  const mapTasks = async () => {
    try {
      for (const id of selectedTaskIds) {
        await mapTaskToSprint({
          sprintId: selectedSprint.sprintId,
          taskId: id,
          orgId,
          createdBy: userId,
        });
      }

      toast.success("Tasks mapped");
      setSelectedTaskIds([]);
      loadSprints();
      openSprintDetails(selectedSprint);
    } catch {
      toast.error("Failed to map tasks");
    }
  };

  const mapTickets = async () => {
    try {
      for (const id of selectedTicketIds) {
        await mapTicketToSprint({
          sprintId: selectedSprint.sprintId,
          ticketId: id,
          orgId,
          createdBy: userId,
        });
      }

      toast.success("Tickets mapped");
      setSelectedTicketIds([]);
      loadSprints();
      openSprintDetails(selectedSprint);
    } catch {
      toast.error("Failed to map tickets");
    }
  };

  const removeTask = async (taskId) => {
    await removeTaskFromSprint(selectedSprint.sprintId, taskId);
    loadSprints();
    openSprintDetails(selectedSprint);
  };

  const removeTicket = async (ticketId) => {
    await removeTicketFromSprint(selectedSprint.sprintId, ticketId);
    loadSprints();
    openSprintDetails(selectedSprint);
  };

  // Collect all unique statuses
  const allStatuses = new Set();

  sprints.forEach((s) => {
    const summary = sprintSummaries[s.sprintId];

    if (!summary) return;

    const statusObj =
      sprintTableViewType === "TASK"
        ? summary.taskStatusSummary
        : summary.ticketStatusSummary;

    if (statusObj) {
      Object.keys(statusObj).forEach((status) => allStatuses.add(status));
    }
  });

  const statusList = Array.from(allStatuses);

  return (
    <div className="fade-in">
      {/* HEADER */}
      <div
        className="d-flex justify-content-between mb-3"
        style={{ margin: "15px" }}
      >
        <h5>Sprint Management</h5>

        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant={
              sprintTableViewType === "TASK" ? "primary" : "outline-primary"
            }
            onClick={() => setSprintTableViewType("TASK")}
          >
            Tasks
          </Button>

          <Button
            size="sm"
            variant={
              sprintTableViewType === "TICKET" ? "primary" : "outline-primary"
            }
            onClick={() => setSprintTableViewType("TICKET")}
          >
            Tickets
          </Button>

          <Button onClick={() => setShowModal(true)}>+ Add Sprint</Button>
        </div>
      </div>

      {/* TABLE */}
      {loading ? (
        <Spinner />
      ) : (
        <Table bordered hover>
          <thead className="table-dark">
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Status</th>
              <th>Type</th>
              <th>Start</th>
              <th>End</th>
              <th>Total</th>
              {/* <th>{sprintTableViewType === "TASK" ? "Completed" : "Closed"}</th> */}
              {statusList.map((status) => (
                <th key={status}>{status}</th>
              ))}{" "}
              <th>Progress</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {sprints.map((s) => {
              const summary = sprintSummaries[s.sprintId] || {};

              const total =
                sprintTableViewType === "TASK"
                  ? summary.taskTotal || 0
                  : summary.ticketTotal || 0;

              {
                /* const done =
                sprintTableViewType === "TASK"
                  ? summary.taskCompleted || 0
                  : summary.ticketClosed || 0; */
              }

              const done =
                sprintTableViewType === "TASK"
                  ? summary.taskStatusSummary?.Completed || 0
                  : summary.ticketStatusSummary?.Closed || 0;

              const progress = total > 0 ? Math.round((done / total) * 100) : 0;

              return (
                <tr key={s.sprintId}>
                  <td>{s.sprintId}</td>
                  <td>{s.sprintName}</td>
                  <td>{s.sprintStatus}</td>
                  <td>{s.sprintTypes}</td>
                  <td>
                    {s.sprintStartDate
                      ? moment(s.sprintStartDate).format("DD MMM YYYY")
                      : "-"}
                  </td>
                  <td>
                    {s.sprintEndDate
                      ? moment(s.sprintEndDate).format("DD MMM YYYY")
                      : "-"}
                  </td>
                  <td>{total}</td>
                  <td>
                    {statusList.map((status) => {
                      const count =
                        sprintTableViewType === "TASK"
                          ? summary.taskStatusSummary?.[status] || 0
                          : summary.ticketStatusSummary?.[status] || 0;

                      return <td key={status}>{count}</td>;
                    })}
                  </td>{" "}
                  <td>
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className="progress-bar bg-success"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <small>{progress}%</small>
                  </td>
                  <td>
                    <Button size="sm" onClick={() => openSprintDetails(s)}>
                      View
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* DETAIL MODAL */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedSprint?.sprintName}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="d-flex gap-2 mb-3">
            <Button
              size="sm"
              variant={
                sprintViewType === "TASK" ? "primary" : "outline-primary"
              }
              onClick={() => setSprintViewType("TASK")}
            >
              Tasks
            </Button>

            <Button
              size="sm"
              variant={
                sprintViewType === "TICKET" ? "primary" : "outline-primary"
              }
              onClick={() => setSprintViewType("TICKET")}
            >
              Tickets
            </Button>
          </div>

          {sprintViewType === "TASK" ? (
            <>
              <Table bordered size="sm">
                <tbody>
                  {sprintTasks.map((t) => (
                    <tr key={t.taskId}>
                      <td>{t.taskName}</td>
                      <td>{t.taskStatus}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => removeTask(t.taskId)}
                        >
                          X
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Form.Select
                multiple
                onChange={(e) =>
                  setSelectedTaskIds(
                    Array.from(e.target.selectedOptions, (o) => o.value),
                  )
                }
              >
                {projectTasks.map((t) => (
                  <option key={t.taskId} value={t.taskId}>
                    {t.taskName}
                  </option>
                ))}
              </Form.Select>

              <Button className="mt-2" onClick={mapTasks}>
                Map Selected Tasks
              </Button>
            </>
          ) : (
            <>
              <Table bordered size="sm">
                <tbody>
                  {sprintTickets.map((t) => (
                    <tr key={t.ticketId}>
                      <td>{t.ticketName}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => removeTicket(t.ticketId)}
                        >
                          X
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Form.Select
                multiple
                onChange={(e) =>
                  setSelectedTicketIds(
                    Array.from(e.target.selectedOptions, (o) => o.value),
                  )
                }
              >
                {projectTickets.map((t) => (
                  <option key={t.ticketId} value={t.ticketId}>
                    {t.ticketName}
                  </option>
                ))}
              </Form.Select>

              <Button className="mt-2" onClick={mapTickets}>
                Map Selected Tickets
              </Button>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* CREATE MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Sprint</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Sprint Name</Form.Label>
            <Form.Control
              value={formData.sprintName}
              onChange={(e) =>
                setFormData({ ...formData, sprintName: e.target.value })
              }
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateSprint} disabled={saving}>
            {saving ? "Creating..." : "Create Sprint"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SprintTab;

// import React, { useEffect, useState } from "react";
// import { Button, Table, Modal, Form, Spinner } from "react-bootstrap";
// import { toast } from "react-toastify";
// import moment from "moment";

// import {
//   createSprint,
//   getSprintsByOrgAndProject,
//   mapTaskToSprint,
//   mapTicketToSprint,
//   getTasksBySprint,
//   getTicketsBySprint,
//   removeTaskFromSprint,
//   removeTicketFromSprint,
//   getSprintSummary,
// } from "../Api/SprintProject";

// import { getTasksByProject } from "../Api/TaskMaster";
// import { getTicketsByProjectId } from "../Api/TicketApiOrg";

// const SprintTab = ({ orgId, projectId, userId }) => {
//   const [sprints, setSprints] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const [showDetailModal, setShowDetailModal] = useState(false);
//   const [saving, setSaving] = useState(false);

//   const [selectedSprint, setSelectedSprint] = useState(null);
//   const [sprintTasks, setSprintTasks] = useState([]);
//   const [sprintTickets, setSprintTickets] = useState([]);

//   const [projectTasks, setProjectTasks] = useState([]);
//   const [projectTickets, setProjectTickets] = useState([]);

//   const [selectedTaskIds, setSelectedTaskIds] = useState([]);
//   const [selectedTicketIds, setSelectedTicketIds] = useState([]);

//   const [sprintViewType, setSprintViewType] = useState("TASK");

//   const [sprintTableViewType, setSprintTableViewType] = useState("TASK");
//   const [sprintSummaries, setSprintSummaries] = useState({});

//   const [formData, setFormData] = useState({
//     sprintName: "",
//     sprintStatus: "Planned",
//     sprintDescription: "",
//     sprintTypes: "",
//     sprintStartDate: "",
//     sprintEndDate: "",
//   });

//   // ================= LOAD SPRINTS =================
//   const loadSprints = async () => {
//     if (!orgId || !projectId) return;

//     try {
//       setLoading(true);

//       const [sprintData, summaryData] = await Promise.all([
//         getSprintsByOrgAndProject(orgId, projectId),
//         getSprintSummary(orgId, projectId),
//       ]);

//       setSprints(sprintData || []);

//       const summaryMap = {};
//       summaryData.forEach((s) => {
//         summaryMap[s.sprintId] = s;
//       });

//       setSprintSummaries(summaryMap);
//     } catch {
//       toast.error("Failed to load sprints");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     loadSprints();
//   }, [orgId, projectId]);

//   // ================= CREATE SPRINT =================
//   const handleCreateSprint = async () => {
//     if (!formData.sprintName.trim()) {
//       toast.error("Sprint name required");
//       return;
//     }

//     try {
//       setSaving(true);

//       await createSprint({
//         orgId: String(orgId),
//         sprintName: formData.sprintName,
//         sprintStatus: formData.sprintStatus,
//         sprintDescription: formData.sprintDescription,
//         sprintProject: projectId,
//         sprintTypes: formData.sprintTypes,
//         sprintStartDate: formData.sprintStartDate,
//         sprintEndDate: formData.sprintEndDate,
//         createdBy: String(userId),
//       });

//       toast.success("Sprint created");
//       setShowModal(false);
//       loadSprints();
//     } catch {
//       toast.error("Failed to create sprint");
//     } finally {
//       setSaving(false);
//     }
//   };

//   // ================= OPEN DETAILS =================
//   const openSprintDetails = async (sprint) => {
//     try {
//       setSelectedSprint(sprint);
//       setShowDetailModal(true);

//       const [tasks, tickets, allTasks, allTickets] = await Promise.all([
//         getTasksBySprint(sprint.sprintId),
//         getTicketsBySprint(sprint.sprintId),
//         getTasksByProject(projectId),
//         getTicketsByProjectId(projectId),
//       ]);

//       console.log("Project Tickets:", allTickets);

//       setSprintTasks(tasks || []);
//       setSprintTickets(tickets || []);
//       setProjectTasks(allTasks || []);
//       setProjectTickets(allTickets || []);
//     } catch {
//       toast.error("Failed to load sprint details");
//     }
//   };

//   // ================= MAP TASK =================
//   const mapTasks = async () => {
//     try {
//       for (const taskId of selectedTaskIds) {
//         await mapTaskToSprint({
//           sprintId: selectedSprint.sprintId,
//           taskId: String(taskId),
//           orgId: String(orgId),
//           createdBy: String(userId),
//         });
//       }

//       toast.success("Tasks mapped");
//       openSprintDetails(selectedSprint);
//       setSelectedTaskIds([]);
//     } catch {
//       toast.error("Failed to map tasks");
//     }
//   };

//   // ================= MAP TICKET =================
//   const mapTickets = async () => {
//     try {
//       for (const ticketId of selectedTicketIds) {
//         await mapTicketToSprint({
//           sprintId: selectedSprint.sprintId,
//           ticketId: String(ticketId),
//           orgId: String(orgId),
//           createdBy: String(userId),
//         });
//       }

//       toast.success("Tickets mapped");
//       openSprintDetails(selectedSprint);
//       setSelectedTicketIds([]);
//     } catch {
//       toast.error("Failed to map tickets");
//     }
//   };

//   // ================= REMOVE =================
//   const removeTask = async (taskId) => {
//     try {
//       await removeTaskFromSprint(selectedSprint.sprintId, taskId);
//       openSprintDetails(selectedSprint);
//     } catch {
//       toast.error("Failed to remove task");
//     }
//   };

//   const removeTicket = async (ticketId) => {
//     try {
//       await removeTicketFromSprint(selectedSprint.sprintId, ticketId);
//       openSprintDetails(selectedSprint);
//     } catch {
//       toast.error("Failed to remove ticket");
//     }
//   };

//   //   const getSprintSummary = (sprintId) => {
//   //     const tasks = sprintTasks.filter((t) => t.sprintId === sprintId);
//   //     const tickets = sprintTickets.filter((t) => t.sprintId === sprintId);

//   //     if (sprintTableViewType === "TASK") {
//   //       const total = tasks.length;

//   //       const summary = {
//   //         total,
//   //         new: tasks.filter((t) => t.taskStatus === "New").length,
//   //         inProgress: tasks.filter((t) => t.taskStatus === "In Progress").length,
//   //         notStarted: tasks.filter((t) => t.taskStatus === "Not Started").length,
//   //         onHold: tasks.filter((t) => t.taskStatus === "On Hold").length,
//   //         completed: tasks.filter((t) => t.taskStatus === "Completed").length,
//   //         cancelled: tasks.filter((t) => t.taskStatus === "Cancelled").length,
//   //       };

//   //       summary.progressPercent =
//   //         total > 0 ? Math.round((summary.completed / total) * 100) : 0;

//   //       return summary;
//   //     } else {
//   //       const total = tickets.length;

//   //       const summary = {
//   //         total,
//   //         open: tickets.filter((t) => t.status === "Open").length,
//   //         inProgress: tickets.filter((t) => t.status === "In Progress").length,
//   //         resolved: tickets.filter((t) => t.status === "Resolved").length,
//   //         closed: tickets.filter((t) => t.status === "Closed").length,
//   //       };

//   //       summary.progressPercent =
//   //         total > 0 ? Math.round((summary.closed / total) * 100) : 0;

//   //       return summary;
//   //     }
//   //   };

//   return (
//     <div className="fade-in">
//       <div className="d-flex justify-content-between align-items-center mb-3">
//         <h5>Sprint Management</h5>

//         <div className="d-flex gap-2">
//           <Button
//             size="sm"
//             variant={
//               sprintTableViewType === "TASK" ? "primary" : "outline-primary"
//             }
//             onClick={() => setSprintTableViewType("TASK")}
//           >
//             Tasks
//           </Button>

//           <Button
//             size="sm"
//             variant={
//               sprintTableViewType === "TICKET" ? "primary" : "outline-primary"
//             }
//             onClick={() => setSprintTableViewType("TICKET")}
//           >
//             Tickets
//           </Button>

//           <Button onClick={() => setShowModal(true)}>+ Add Sprint</Button>
//         </div>
//       </div>

//       {loading ? (
//         <Spinner />
//       ) : (
//         <Table bordered hover>
//           <thead className="table-dark">
//             <tr>
//               <th>ID</th>
//               <th>Name</th>
//               <th>Status</th>
//               <th>Type</th>
//               <th>Start</th>
//               <th>End</th>
//               <th>Total</th>

//               {sprintTableViewType === "TASK" ? (
//                 <th>Completed</th>
//               ) : (
//                 <th>Closed</th>
//               )}

//               <th>Progress</th>
//               <th>Action</th>
//             </tr>
//           </thead>
//           <tbody>
//             {sprints.map((s) => {
//               const summary = sprintSummaries[s.sprintId] || {};
//               console.log(`Sprint ${s.sprintName} Summary:`, summary);

//               const progress =
//                 sprintTableViewType === "TASK"
//                   ? summary.taskTotal > 0
//                     ? Math.round(
//                         (summary.taskCompleted / summary.taskTotal) * 100,
//                       )
//                     : 0
//                   : summary.ticketTotal > 0
//                     ? Math.round(
//                         (summary.ticketClosed / summary.ticketTotal) * 100,
//                       )
//                     : 0;

//               return (
//                 <tr key={s.sprintId}>
//                   <td>{s.sprintId}</td>
//                   <td>{s.sprintName}</td>
//                   <td>{s.sprintStatus}</td>
//                   <td>{s.sprintTypes}</td>
//                   <td>
//                     {s.sprintStartDate
//                       ? moment(s.sprintStartDate).format("DD MMM YYYY")
//                       : "-"}
//                   </td>
//                   <td>
//                     {s.sprintEndDate
//                       ? moment(s.sprintEndDate).format("DD MMM YYYY")
//                       : "-"}
//                   </td>

//                   {/* TOTAL */}
//                   <td>
//                     {sprintTableViewType === "TASK"
//                       ? summary.taskTotal || 0
//                       : summary.ticketTotal || 0}
//                   </td>

//                   {/* COMPLETED / CLOSED */}
//                   <td>
//                     {sprintTableViewType === "TASK"
//                       ? summary.taskCompleted || 0
//                       : summary.ticketClosed || 0}
//                   </td>

//                   {/* PROGRESS */}
//                   <td>
//                     <div className="progress" style={{ height: "8px" }}>
//                       <div
//                         className="progress-bar bg-success"
//                         style={{ width: `${progress}%` }}
//                       ></div>
//                     </div>
//                     <small>{progress}%</small>
//                   </td>

//                   <td>
//                     <Button size="sm" onClick={() => openSprintDetails(s)}>
//                       View
//                     </Button>
//                   </td>
//                 </tr>
//               );
//             })}
//           </tbody>
//         </Table>
//       )}

//       {/* DETAIL MODAL */}
//       <Modal
//         show={showDetailModal}
//         onHide={() => setShowDetailModal(false)}
//         size="xl"
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>{selectedSprint?.sprintName}</Modal.Title>
//         </Modal.Header>

//         <div className="d-flex gap-2 mb-3">
//           <Button
//             size="sm"
//             variant={sprintViewType === "TASK" ? "primary" : "outline-primary"}
//             onClick={() => setSprintViewType("TASK")}
//           >
//             Tasks
//           </Button>

//           <Button
//             size="sm"
//             variant={
//               sprintViewType === "TICKET" ? "primary" : "outline-primary"
//             }
//             onClick={() => setSprintViewType("TICKET")}
//           >
//             Tickets
//           </Button>
//         </div>
//         <Modal.Body>
//           {sprintViewType === "TASK" && (
//             <>
//               <h6>Mapped Tasks</h6>
//               <Table bordered size="sm">
//                 <thead>
//                   <tr>
//                     <th>Name</th>
//                     <th>Status</th>
//                     <th>Remove</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {sprintTasks.map((t) => (
//                     <tr key={t.taskId}>
//                       <td>{t.taskName}</td>
//                       <td>{t.taskStatus}</td>
//                       <td>
//                         <Button
//                           size="sm"
//                           variant="danger"
//                           onClick={() => removeTask(t.taskId)}
//                         >
//                           X
//                         </Button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </Table>

//               <Form.Select
//                 multiple
//                 onChange={(e) =>
//                   setSelectedTaskIds(
//                     Array.from(e.target.selectedOptions, (o) => o.value),
//                   )
//                 }
//               >
//                 {projectTasks.map((t) => (
//                   <option key={t.taskId} value={t.taskId}>
//                     {t.taskName}
//                   </option>
//                 ))}
//               </Form.Select>

//               <Button className="mt-2" onClick={mapTasks}>
//                 Map Selected Tasks
//               </Button>
//             </>
//           )}

//           <hr />

//           {sprintViewType === "TICKET" && (
//             <>
//               <h6>Mapped Tickets</h6>
//               <Table bordered size="sm">
//                 <thead>
//                   <tr>
//                     <th>Title</th>
//                     <th>Remove</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {sprintTickets.map((t) => (
//                     <tr key={t.ticketId}>
//                       <td>{t.ticketName}</td>
//                       <td>
//                         <Button
//                           size="sm"
//                           variant="danger"
//                           onClick={() => removeTicket(t.ticketId)}
//                         >
//                           X
//                         </Button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </Table>

//               <Form.Select
//                 multiple
//                 onChange={(e) =>
//                   setSelectedTicketIds(
//                     Array.from(e.target.selectedOptions, (o) => o.value),
//                   )
//                 }
//               >
//                 {projectTickets.map((t) => (
//                   <option key={t.ticketId} value={t.ticketId}>
//                     {t.ticketName}
//                   </option>
//                 ))}
//               </Form.Select>

//               <Button className="mt-2" onClick={mapTickets}>
//                 Map Selected Tickets
//               </Button>
//             </>
//           )}
//         </Modal.Body>
//       </Modal>

//       {/* CREATE MODAL */}
//       {/* CREATE MODAL */}
//       <Modal show={showModal} onHide={() => setShowModal(false)}>
//         <Modal.Header closeButton>
//           <Modal.Title>Create Sprint</Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           <Form.Group className="mb-3">
//             <Form.Label>Sprint Name</Form.Label>
//             <Form.Control
//               value={formData.sprintName}
//               onChange={(e) =>
//                 setFormData({ ...formData, sprintName: e.target.value })
//               }
//             />
//           </Form.Group>

//           <Form.Group className="mb-3">
//             <Form.Label>Status</Form.Label>
//             <Form.Select
//               value={formData.sprintStatus}
//               onChange={(e) =>
//                 setFormData({ ...formData, sprintStatus: e.target.value })
//               }
//             >
//               <option>Planned</option>
//               <option>Active</option>
//               <option>Completed</option>
//               <option>On Hold</option>
//             </Form.Select>
//           </Form.Group>

//           <Form.Group className="mb-3">
//             <Form.Label>Type</Form.Label>
//             <Form.Control
//               value={formData.sprintTypes}
//               onChange={(e) =>
//                 setFormData({ ...formData, sprintTypes: e.target.value })
//               }
//             />
//           </Form.Group>

//           <Form.Group className="mb-3">
//             <Form.Label>Start Date</Form.Label>
//             <Form.Control
//               type="date"
//               value={formData.sprintStartDate}
//               onChange={(e) =>
//                 setFormData({ ...formData, sprintStartDate: e.target.value })
//               }
//             />
//           </Form.Group>

//           <Form.Group className="mb-3">
//             <Form.Label>End Date</Form.Label>
//             <Form.Control
//               type="date"
//               value={formData.sprintEndDate}
//               onChange={(e) =>
//                 setFormData({ ...formData, sprintEndDate: e.target.value })
//               }
//             />
//           </Form.Group>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={() => setShowModal(false)}>
//             Cancel
//           </Button>

//           <Button onClick={handleCreateSprint} disabled={saving}>
//             {saving ? "Creating..." : "Create Sprint"}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </div>
//   );
// };

// export default SprintTab;
